// Сетевой слой клиента (E01-T08, tech/03 §1): типы запросов и ответов берутся ТОЛЬКО из @garages/contract.
// Здесь: параметры пути, подпись X-Req-* (новая на каждую попытку), Idempotency-Key (один на все попытки),
// таймаут 10 с, повторы 1/2/4 с для 5xx и обрывов, Retry-After, обновление токена по 401, время сервера.
// Что показать игроку по коду — net/error-policy.ts.
import { track } from '../analytics/analytics.js';
import {
  API_PREFIX, ErrorBody, Header, api, signRequest,
  type Api, type BodyOf, type EndpointName, type ParamsOf, type QueryOf, type ResponseOf,
} from '@garages/contract';
import type { AnyErrorCode } from './error-policy.js';

/** api_error (tech/09 §3.9): ошибка запроса — в аналитику; сам запрос и тело не пишутся. */
function noteError(e: ApiCallError): ApiCallError {
  track('api_error', { endpoint: e.endpoint ?? '', status: e.status, code: e.code, retry: e.retryable });
  return e;
}

export class ApiCallError extends Error {
  constructor(
    readonly status: number,
    readonly code: AnyErrorCode,
    readonly details?: Record<string, unknown>,
    readonly retryable = false,
    /** Секунды из Retry-After, если сервер их прислал. */
    readonly retryAfterS?: number,
    readonly endpoint?: EndpointName,
  ) {
    super(code);
  }
}

export interface Session {
  accessToken: string;
  sessionKey: string;
}

export interface CallOptions<N extends EndpointName> {
  params?: ParamsOf<Api[N]>;
  query?: QueryOf<Api[N]>;
  body?: BodyOf<Api[N]>;
  /** UUID v7; обязателен для эндпоинтов с idempotency = 'key-required'. Один ключ на все повторы. */
  idempotencyKey?: string;
  /** Не повторять автоматически (экран сам предложит «Повторить»). */
  noRetry?: boolean;
  signal?: AbortSignal;
  /** Свой таймаут запроса (ход боя — 4 с, tech/04 §9). */
  timeoutMs?: number;
}

export interface ApiClientDeps {
  fetch: typeof fetch;
  session: () => Session | null;
  clientBuild: string;
  now?: () => number;
  nonce?: () => string;
  /** Без сервера: ответы из фикстур договора (VITE_USE_FIXTURES=true без мок-сервера). */
  fixtures?: Partial<{ [K in EndpointName]: ResponseOf<Api[K]> }>;
  /** Обновить access по refresh (01 §2). true — можно повторить запрос. */
  refresh?: () => Promise<boolean>;
  /** Задержки повторов для 5xx и обрывов, мс (ux/11 §4.1 «1, 2, 4…»). */
  retryDelaysMs?: readonly number[];
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
  /** Удачный или неудачный сетевой ответ — для ST-OFFLINE (два подряд неудачных → офлайн). */
  onNetwork?: (ok: boolean) => void;
  /** X-Server-Time ответа, мс — для таймеров по времени сервера (tech/03 §1 «Время»). */
  onServerTime?: (serverMs: number, localMs: number) => void;
}

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export function ulid(now = Date.now(), rand: () => number = Math.random): string {
  let t = now;
  let time = '';
  for (let i = 0; i < 10; i++) { time = CROCKFORD[t % 32] + time; t = Math.floor(t / 32); }
  let r = '';
  for (let i = 0; i < 16; i++) r += CROCKFORD[Math.floor(rand() * 32)];
  return time + r;
}

/** UUID v7 в нижнем регистре (tech/03 §1 Idempotency-Key): 48 бит времени, версия 7, вариант 10. */
export function uuid7(now = Date.now(), rand: () => number = Math.random): string {
  const hex = now.toString(16).padStart(12, '0').slice(-12);
  const r = (n: number) => Array.from({ length: n }, () => Math.floor(rand() * 16).toString(16)).join('');
  const variant = (8 + Math.floor(rand() * 4)).toString(16);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-7${r(3)}-${variant}${r(3)}-${r(12)}`;
}

export function buildPath(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = params?.[k];
    if (v === undefined) throw new Error(`нет параметра пути ${k}`);
    return encodeURIComponent(String(v));
  });
}

export const DEFAULT_RETRY_DELAYS_MS = [1000, 2000, 4000] as const;
export const CLIENT_TIMEOUT_MS = 10_000;

/** Повторяем автоматически только то, что сервер гасит сам: 500, 503 UNAVAILABLE, 429, обрыв и таймаут. */
export function isAutoRetryable(e: ApiCallError): boolean {
  return e.code === 'NETWORK' || e.code === 'TIMEOUT' || e.code === 'INTERNAL' || e.code === 'UNAVAILABLE' || e.code === 'RATE_LIMITED';
}

export type ApiCall = <N extends EndpointName>(name: N, opts?: CallOptions<N>) => Promise<ResponseOf<Api[N]>>;

export function createApiClient(deps: ApiClientDeps): ApiCall {
  const now = deps.now ?? Date.now;
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const delays = deps.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  const timeoutMs = deps.timeoutMs ?? CLIENT_TIMEOUT_MS;

  async function once<N extends EndpointName>(name: N, opts: CallOptions<N>): Promise<ResponseOf<Api[N]>> {
    const ep = api[name];
    const qs = opts.query ? `?${new URLSearchParams(Object.entries(opts.query as Record<string, unknown>).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])).toString()}` : '';
    const path = API_PREFIX + buildPath(ep.path, opts.params as Record<string, unknown> | undefined) + qs;
    const body = ep.method === 'GET' ? undefined : JSON.stringify(opts.body ?? {});
    const headers: Record<string, string> = { [Header.clientBuild]: deps.clientBuild };
    if (body !== undefined) headers['content-type'] = 'application/json';
    const s = deps.session();
    if (ep.auth) {
      if (!s) throw new ApiCallError(401, 'AUTH_REQUIRED', undefined, false, undefined, name);
      headers[Header.authorization] = `Bearer ${s.accessToken}`;
    }
    if (ep.signed && s) {
      const ts = now();
      const nonce = deps.nonce?.() ?? ulid(ts);
      headers[Header.reqTs] = String(ts);
      headers[Header.reqNonce] = nonce;
      headers[Header.reqSig] = await signRequest({ sessionKey: s.sessionKey, method: ep.method, path, ts, nonce, body: body ?? '' });
    }
    if (opts.idempotencyKey) headers[Header.idempotencyKey] = opts.idempotencyKey;

    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? timeoutMs);
    opts.signal?.addEventListener('abort', () => ctl.abort());
    let res: Response;
    try {
      res = await deps.fetch(path, { method: ep.method, headers, signal: ctl.signal, ...(body !== undefined ? { body } : {}) });
    } catch {
      deps.onNetwork?.(false);
      const timedOut = ctl.signal.aborted && !opts.signal?.aborted;
      throw noteError(new ApiCallError(0, timedOut ? 'TIMEOUT' : 'NETWORK', undefined, true, undefined, name));
    } finally {
      clearTimeout(timer);
    }
    deps.onNetwork?.(true);
    const st = Number(res.headers.get(Header.serverTime));
    if (st > 0) deps.onServerTime?.(st, now());
    if (res.status === 204 && ep.allowNoContent) return undefined as ResponseOf<Api[N]>;
    const json: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      const ra = Number(res.headers.get(Header.retryAfter));
      const retryAfterS = Number.isFinite(ra) && ra > 0 ? ra : undefined;
      const e = ErrorBody.safeParse(json);
      if (e.success) throw noteError(new ApiCallError(res.status, e.data.error.code, e.data.error.details, e.data.error.retryable, retryAfterS, name));
      throw noteError(new ApiCallError(res.status, res.status >= 500 ? 'INTERNAL' : 'BAD_RESPONSE', undefined, res.status >= 500, retryAfterS, name));
    }
    const parsed = ep.response.safeParse(json);
    if (!parsed.success) throw noteError(new ApiCallError(res.status, 'BAD_RESPONSE', { issues: parsed.error.issues.slice(0, 5) }, false, undefined, name));
    return parsed.data as ResponseOf<Api[N]>;
  }

  return async function call<N extends EndpointName>(name: N, opts: CallOptions<N> = {}): Promise<ResponseOf<Api[N]>> {
    const ep = api[name];
    if (deps.fixtures && name in deps.fixtures) return ep.response.parse(deps.fixtures[name]) as ResponseOf<Api[N]>;
    if (ep.idempotency === 'key-required' && !opts.idempotencyKey) throw new Error(`${ep.id}: нужен Idempotency-Key (tech/03 §1)`);
    // Без ключа повтор «key»-эндпоинта (очки характеристик) мог бы применить действие дважды — не повторяем.
    const canRetry = !opts.noRetry && !(ep.idempotency === 'key' && !opts.idempotencyKey);
    let refreshed = false;
    for (let attempt = 0; ; attempt++) {
      try {
        return await once(name, opts);
      } catch (err) {
        if (!(err instanceof ApiCallError)) throw err;
        if ((err.code === 'TOKEN_EXPIRED' || err.code === 'AUTH_REQUIRED') && ep.auth && !refreshed && deps.refresh) {
          refreshed = true;
          if (await deps.refresh()) { attempt--; continue; }
        }
        if (!canRetry || !isAutoRetryable(err) || attempt >= delays.length) throw err;
        await sleep(Math.max(delays[attempt]!, (err.retryAfterS ?? 0) * 1000));
      }
    }
  };
}
